import { useEffect, useRef, useState, type MutableRefObject, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import {
    type OfficialStatementData,
    type OfficialStatementUpdateTarget,
    useOfficialStatement,
} from "./official-statement-store";
import adPoliceLogoUrl from "../assets/ad-police-logo.png";

const HIGHLIGHT_DURATION_MS = 1500;

export function OfficialStatementPanel() {
    const { t } = useTranslation();
    const { statement, lastUpdate } = useOfficialStatement();
    const [highlightedTarget, setHighlightedTarget] =
        useState<OfficialStatementUpdateTarget | null>(null);
    const declarantRef = useRef<HTMLElement>(null);
    const investigationRefs = useRef<Array<HTMLLIElement | null>>([]);
    const attachmentsRef = useRef<HTMLElement>(null);
    const lockedRef = useRef<HTMLDivElement>(null);

    useEffect(
        function applyUpdateHighlight() {
            if (!lastUpdate) {
                setHighlightedTarget(null);
                return;
            }

            const target = lastUpdate.target;
            const timer = window.setTimeout(function clearHighlight() {
                setHighlightedTarget(null);
            }, HIGHLIGHT_DURATION_MS);

            window.queueMicrotask(function scrollToTarget() {
                const element = resolveElement(target);
                element?.scrollIntoView({ behavior: "smooth", block: "center" });
                setHighlightedTarget(target);
            });

            return function cleanupHighlight() {
                window.clearTimeout(timer);
            };
        },
        [lastUpdate],
    );

    function resolveElement(target: OfficialStatementUpdateTarget) {
        if (target === "declarant") {
            return declarantRef.current;
        }

        if (target === "attachments") {
            return attachmentsRef.current;
        }

        if (target === "locked") {
            return lockedRef.current;
        }

        if (target.startsWith("investigation:")) {
            const index = Number(target.slice("investigation:".length));
            return Number.isInteger(index) ? investigationRefs.current[index] : null;
        }

        return null;
    }

    return (
        <aside
            className={`min-h-0 min-w-0 overflow-hidden bg-neutral-100 ${
                statement
                    ? "border-t border-zinc-200 lg:border-s lg:border-t-0"
                    : ""
            }`}
            aria-hidden={!statement}
        >
            <div className="h-full overflow-y-auto p-4">
                {statement && (
                    <StatementDocument
                        statement={statement}
                        t={t}
                        highlightedTarget={highlightedTarget}
                        declarantRef={declarantRef}
                        investigationRefs={investigationRefs}
                        attachmentsRef={attachmentsRef}
                        lockedRef={lockedRef}
                    />
                )}
            </div>
        </aside>
    );
}

type StatementDocumentProps = {
    statement: OfficialStatementData;
    t: (key: string) => string;
    highlightedTarget: OfficialStatementUpdateTarget | null;
    declarantRef: RefObject<HTMLElement | null>;
    investigationRefs: MutableRefObject<Array<HTMLLIElement | null>>;
    attachmentsRef: RefObject<HTMLElement | null>;
    lockedRef: RefObject<HTMLDivElement | null>;
};

function StatementDocument({
    statement,
    t,
    highlightedTarget,
    declarantRef,
    investigationRefs,
    attachmentsRef,
    lockedRef,
}: StatementDocumentProps) {
    return (
        <article className="mx-auto max-w-[720px] overflow-hidden border border-neutral-300 bg-white text-neutral-900 shadow-[0_1px_3px_rgba(0,0,0,0.04),0_12px_32px_-12px_rgba(0,0,0,0.12)]">
            <div className="h-1 bg-neutral-900" />

            <header
                className="flex items-center justify-between gap-4 border-b border-neutral-200 px-7 py-6"
                dir="ltr"
            >
                <div className="flex-1 text-left" dir="ltr">
                    <h1 className="text-[15px] font-semibold leading-tight">
                        United Arab Emirates
                    </h1>
                    <p className="mt-1 text-[12px] font-medium leading-tight text-neutral-500">
                        Ministry of Interior
                    </p>
                    <p className="mt-1 text-[12px] font-medium leading-tight text-neutral-500">
                        {t("report.letterhead.unitEn")}
                    </p>
                </div>
                <div className="flex size-14 shrink-0 items-center justify-center rounded-sm">
                    <img
                        src={adPoliceLogoUrl}
                        alt=""
                        className="size-14 object-contain"
                    />
                </div>
                <div className="flex-1 text-right" dir="rtl">
                    <h1 className="text-[16px] font-semibold leading-tight">
                        دولة الإمارات العربية المتحدة
                    </h1>
                    <p className="mt-1 text-[12px] font-medium leading-tight text-neutral-500">
                         شرطة عجمان
                    </p>
                    <p className="mt-1 text-[12px] font-medium leading-tight text-neutral-500">
                        {t("report.letterhead.unitAr")}
                    </p>
                </div>
            </header>

            <section className="border-b border-neutral-200 px-7 py-5 text-start">
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-neutral-500">
                            {t("report.kicker")}
                        </p>
                        <h2 className="mt-2 text-[24px] font-semibold leading-tight">
                            {t("report.title")}
                        </h2>
                        <p className="mt-1 text-[13px] text-neutral-500">
                            {t("report.caption")}
                        </p>
                    </div>

                    <div
                        ref={lockedRef}
                        className={`relative mt-1 flex shrink-0 items-center gap-2 rounded-full border px-3 py-1 ltr:flex-row rtl:flex-row-reverse ${
                            statement.locked
                                ? "border-neutral-300 bg-neutral-100 text-neutral-700"
                                : "border-emerald-200 bg-emerald-50 text-emerald-800"
                        }`}
                    >
                        <span
                            className={`size-1.5 rounded-full ${
                                statement.locked ? "bg-neutral-600" : "bg-emerald-600"
                            }`}
                        />
                        <span className="text-[11px] font-semibold">
                            {statement.locked
                                ? t("report.status.locked")
                                : t("report.status.recording")}
                        </span>
                        {highlightedTarget === "locked" && <Highlight />}
                    </div>
                </div>
            </section>

            <dl className="grid grid-cols-2">
                <MetaItem label={t("report.meta.reference")} value={statement.reference} mono border />
                <MetaItem label={t("report.meta.caseType")} value={statement.caseType} />
                <MetaItem label={t("report.meta.date")} value={statement.dateOfRecord} border />
                <MetaItem label={t("report.meta.time")} value={statement.timeOfRecord} mono />
            </dl>

            <section ref={declarantRef} className="relative border-t border-neutral-200">
                <SectionHeader number="01" title={t("report.sections.declarant")} label={t("report.sectionLabel")} />
                <dl className="divide-y divide-neutral-200">
                    <Field label={t("report.fields.fullName")} value={statement.declarant.fullName} />
                    <Field label={t("report.fields.address")} value={statement.declarant.address} />
                    <Field label={t("report.fields.contactMethod")} value={statement.declarant.contactMethod} />
                    <Field label={t("report.fields.contactNumber")} value={statement.declarant.contactNumber} mono />
                </dl>
                {highlightedTarget === "declarant" && <Highlight />}
            </section>

            <section className="border-t border-neutral-200">
                <SectionHeader number="02" title={t("report.sections.investigation")} label={t("report.sectionLabel")} />
                {statement.investigation.length === 0 ? (
                    <div className="px-7 py-12 text-center">
                        <p className="text-[13px] font-semibold text-neutral-800">
                            {t("report.emptyInvestigation.title")}
                        </p>
                        <p className="mx-auto mt-1 max-w-[300px] text-[12px] leading-relaxed text-neutral-500">
                            {t("report.emptyInvestigation.description")}
                        </p>
                    </div>
                ) : (
                    <ol className="divide-y divide-neutral-200">
                        {statement.investigation.map(function renderEntry(qa, index) {
                            return (
                                <li
                                    key={`${qa.question}-${index}`}
                                    ref={function setEntryRef(element) {
                                        investigationRefs.current[index] = element;
                                    }}
                                    className="relative px-7 py-5"
                                >
                                    <div className="flex items-start gap-3">
                                        <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center bg-neutral-900 font-mono text-[10px] font-semibold text-white">
                                            {String(index + 1).padStart(2, "0")}
                                        </span>
                                        <div className="min-w-0 flex-1 space-y-3">
                                            <div>
                                                <p className="text-[10px] font-semibold tracking-wide text-neutral-500">
                                                    {t("report.question")}
                                                </p>
                                                <p className="mt-1 text-[14px] font-medium leading-snug">
                                                    {qa.question}
                                                </p>
                                            </div>
                                            <div className="border-s-2 border-neutral-300 ps-3">
                                                <p className="text-[10px] font-semibold tracking-wide text-neutral-500">
                                                    {t("report.response")}
                                                </p>
                                                <p className="mt-1 text-[14px] leading-relaxed text-neutral-700">
                                                    {qa.answer}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    {highlightedTarget === `investigation:${index}` && (
                                        <Highlight />
                                    )}
                                </li>
                            );
                        })}
                    </ol>
                )}
            </section>

            <section ref={attachmentsRef} className="relative border-t border-neutral-200">
                <SectionHeader number="03" title={t("report.sections.attachments")} label={t("report.sectionLabel")} />
                <div className="px-7 py-5">
                    {statement.attachments && statement.attachments.length > 0 ? (
                        <ul className="space-y-2">
                            {statement.attachments.map(function renderAttachment(
                                attachment,
                                index,
                            ) {
                                return (
                                    <li
                                        key={`${attachment.description}-${index}`}
                                        className="border border-neutral-200 bg-neutral-50 px-3 py-2"
                                    >
                                        <p className="text-[13px] text-neutral-800">
                                            {attachment.description}
                                        </p>
                                        <p className="mt-1 text-[11px] text-neutral-500">
                                            {attachment.note}
                                        </p>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <p className="text-[13px] text-neutral-500">
                            {t("report.noAttachments")}
                        </p>
                    )}
                </div>
                {highlightedTarget === "attachments" && <Highlight />}
            </section>

            <section className="border-t border-neutral-200">
                <SectionHeader number="04" title={t("report.sections.attestation")} label={t("report.sectionLabel")} />
                <div className="px-7 py-5">
                    <p className="text-[13px] leading-relaxed text-neutral-700">
                        {t("report.attestation")}
                    </p>
                    <div className="mt-6">
                        <p className="text-[10px] font-medium tracking-wide text-neutral-500">
                            {t("report.declarantSignature")}
                        </p>
                        <p className="mt-1 text-[12px] text-neutral-700">
                            {statement.declarantName || statement.declarant.fullName}
                        </p>
                    </div>
                </div>
            </section>

            <footer className="flex items-center justify-between gap-3 border-t border-neutral-200 bg-neutral-50 px-7 py-3">
                <span className="font-mono text-[10px] tracking-wider text-neutral-500">
                    {statement.reference}
                </span>
                <span className="text-[10px] font-medium tracking-wide text-neutral-500">
                    {t("report.page")}
                </span>
                <span className="text-[11px] text-neutral-500">
                    {t("report.confidential")}
                </span>
            </footer>
        </article>
    );
}

type MetaItemProps = {
    label: string;
    value: string;
    mono?: boolean;
    border?: boolean;
};

function MetaItem({ label, value, mono = false, border = false }: MetaItemProps) {
    return (
        <div
            className={`border-b border-neutral-200 px-7 py-4 ${
                border ? "border-e" : ""
            }`}
        >
            <dt className="text-[10px] font-medium tracking-wide text-neutral-500">
                {label}
            </dt>
            <dd
                className={`mt-1 text-[13px] font-medium ${
                    mono ? "font-mono" : ""
                }`}
                dir={mono ? "ltr" : undefined}
            >
                {value || "-"}
            </dd>
        </div>
    );
}

type FieldProps = {
    label: string;
    value: string;
    mono?: boolean;
};

function Field({ label, value, mono = false }: FieldProps) {
    return (
        <div className="grid grid-cols-[130px_1fr] items-baseline gap-4 px-7 py-4">
            <dt className="text-[11px] font-medium tracking-wide text-neutral-500">
                {label}
            </dt>
            <dd
                className={`min-h-5 text-[14px] text-neutral-900 ${
                    mono ? "font-mono text-[13px]" : ""
                }`}
                dir={mono ? "ltr" : undefined}
            >
                {value || "-"}
            </dd>
        </div>
    );
}

type SectionHeaderProps = {
    number: string;
    title: string;
    label: string;
};

function SectionHeader({ number, title, label }: SectionHeaderProps) {
    return (
        <header className="flex items-baseline gap-3 border-b border-neutral-200 bg-neutral-50 px-7 py-3">
            <span className="font-mono text-[10px] font-medium tracking-[0.14em] text-neutral-500">
                {label} · {number}
            </span>
            <h3 className="text-[14px] font-semibold">{title}</h3>
        </header>
    );
}

function Highlight() {
    return (
        <div className="pointer-events-none absolute inset-0 z-10 animate-report-flash bg-amber-300" />
    );
}
